import { applicationSubmit, ApproveType, doApprove } from "@/apis/reimburse";
import { RootState } from "@/store";
import { SConModal } from "@/styles/SConModal";
import { Org } from "@/types/org";
import { PageId } from "@/types/page";
import {
  ApproveStatusEnum,
  ReimbursRequest,
  ReimbursRequestStep,
  AssignTypeEnum,
  ApproveTypeEnum,
  ApproveStepStatusEnum,
} from "@/types/reimburse";
import {
  getApplicationTypeInfo,
  getApproveStatusInfo,
} from "../../utils/approveStatus";
import { User } from "@/types/user";
import {
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  notification,
  Space,
  Tag,
} from "antd";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

interface ApproveFooterProps {
  detail: ReimbursRequest;
  onConfirm: (
    data: ReimbursRequest,
    confirmType: ApproveType | undefined,
  ) => void;
  onSave: () => Promise<ReimbursRequest>;
  setHasUpdate: (val: boolean) => void;
  pageId?: PageId;
  hasUpdate: boolean;
}

const ApproveHeader: FC<ApproveFooterProps> = ({
  detail,
  onConfirm,
  onSave,
  setHasUpdate,
  pageId,
  hasUpdate,
}) => {
  const { t } = useTranslation();

  const currentOrg: Org = useSelector(
    (state: RootState) => state.orgReducer.curOrg,
  );
  const currentUser: User = useSelector(
    (state: RootState) => state.userReducer.curUser,
  );

  const [loading, setLoading] = useState<ApproveType | undefined>(undefined);
  const [approveComment, setApproveComment] = useState<string>("");

  const [submiting, setSubmiting] = useState<boolean>(false);

  const info = useMemo(() => {
    const statusInfo = getApproveStatusInfo(detail.status);
    const typeInfo = getApplicationTypeInfo(detail.type);

    return {
      statusColor: statusInfo?.color || "default",
      statusText: statusInfo ? t(statusInfo.label) : "-",
      typeText: typeInfo ? t(typeInfo.label) : "-",
      typeColor: typeInfo?.color || "default",
    };
  }, [detail.status, detail.type, t]);

  const matchStep: (step: ReimbursRequestStep) => boolean = (step) => {
    if (
      [
        AssignTypeEnum.SPECIFIC_ORG_ANYONE,
        AssignTypeEnum.APPLICANT_ORG_ANYONE,
      ].includes(step.assignType)
    )
      return !!currentOrg.id && currentOrg.id === step.assignOrgId;
    if (step.assignType === AssignTypeEnum.SPECIFIC_USERS)
      return !!step.candidates?.find((u) => u.id === currentUser.id);
    return false;
  };

  const myStep: ReimbursRequestStep | null = useMemo(() => {
    let myStep: ReimbursRequestStep | null = null;
    if (detail.approveType === ApproveTypeEnum.ANY)
      myStep = detail?.steps?.find(matchStep) || null;

    if (
      detail.approveType &&
      [ApproveTypeEnum.IN_ORDER, ApproveTypeEnum.LAST].includes(
        detail.approveType,
      )
    ) {
      myStep =
        detail?.steps
          ?.sort((a, b) => a.stepNo - b.stepNo)
          .find((step) => step.stepStatus === ApproveStepStatusEnum.PENDING) ||
        null;
    }
    return myStep;
  }, [detail]);

  const handleApprove = () => {
    if (myStep && loading) {
      doApprove(myStep.id, loading, approveComment)
        .then((res) => {
          notification.success({
            placement: "top",
            message:
              loading === "PASS"
                ? t("reimburse.passSuccess")
                : loading === "REJECT"
                  ? t("reimburse.rejectSuccess")
                  : loading === "RETURN"
                    ? t("reimburse.returnSuccess")
                    : t("order.operationSucc"),
          });
          onConfirm(
            {
              ...detail,
              steps:
                detail.steps?.map((step) =>
                  step.id === res.id ? { ...res } : { ...step },
                ) || null,
            },
            loading,
          );
        })
        .catch((err) => {
          notification.error({
            placement: "top",
            message: t("common.operationFailed"),
            description:
              loading === "PASS"
                ? t("reimburse.passFailed")
                : loading === "REJECT"
                  ? t("reimburse.rejectFailed")
                  : loading === "RETURN"
                    ? t("reimburse.returnFailed")
                    : "",
          });
        })
        .finally(() => setLoading(undefined));
    }
  };

  const handleSubmit = async () => {
    // 校验 title
    if (!detail.title || detail.title.trim() === "") {
      notification.error({
        placement: "top",
        message: t("common.tip"),
        description: t("reimburse.plTitle"),
      });
      return;
    }
    // 校验 content
    if (!detail.content || detail.content.trim() === "") {
      notification.error({
        placement: "top",
        message: t("common.tip"),
        description: t("reimburse.plContent"),
      });
      return;
    }
    if (hasUpdate) await onSave();
    setHasUpdate(false);
    setSubmiting(true);
    applicationSubmit(detail.status === ApproveStatusEnum.RETURNED, detail.id)
      .then((res) => {
        notification.success({
          placement: "top",
          message: t("reimburse.submitSuccess"),
        });
        onConfirm(res, undefined);
      })
      .catch((err) => {
        notification.error({
          placement: "top",
          message: t("reimburse.submitFailed"),
        });
      })
      .finally(() => setSubmiting(false));
  };

  const handleSave = async () => {
    if (!detail.title || detail.title.trim() === "") {
      notification.error({
        placement: "top",
        message: t("common.tip"),
        description: t("reimburse.plTitle"),
      });
      return;
    }
    // 校验 content
    if (!detail.content || detail.content.trim() === "") {
      notification.error({
        placement: "top",
        message: t("common.tip"),
        description: t("reimburse.plContent"),
      });
      return;
    }
    try {
      let res = await onSave();
      setHasUpdate(false);
      onConfirm(res, undefined);
      notification.success({ placement: "top", message: t("common.saveOk") });
    } catch (error) {
      notification.error({ placement: "top", message: t("common.saveFAil") });
    }
  };

  return (
    <Flex align="center" gap={8} style={{ width: "100%" }}>
      <Tag color="geekblue">{detail.number}</Tag>
      <Tag color={info.typeColor}>{info.typeText}</Tag>
      <Tag color={info.statusColor}>{info.statusText}</Tag>
      {pageId === PageId.REIMBURSE_APPROVE && myStep && (
        <Space>
          <Divider type="vertical" style={{ borderColor: "#000" }} />
          <Button
            onClick={() => setLoading("PASS")}
            disabled={loading && loading !== "PASS"}
            loading={loading === "PASS"}
            type="primary"
          >
            {t("reimburse.pass")}
          </Button>
          <Button
            onClick={() => setLoading("REJECT")}
            disabled={loading && loading !== "REJECT"}
            loading={loading === "REJECT"}
            type="link"
            danger
          >
            {t("reimburse.reject")}
          </Button>
          <Button
            onClick={() => setLoading("RETURN")}
            disabled={loading && loading !== "RETURN"}
            loading={loading === "RETURN"}
            type="link"
            style={{ color: "orange" }}
          >
            {t("reimburse.return")}
          </Button>
        </Space>
      )}

      {pageId === PageId.REIMBURSE_MINE &&
        [ApproveStatusEnum.DRAFT, ApproveStatusEnum.RETURNED].includes(
          detail.status,
        ) && (
          <Space>
            <Divider type="vertical" style={{ borderColor: "#000" }} />
            <Button
              disabled={!hasUpdate}
              onClick={() => handleSave()}
              type="link"
            >
              {t("common.save")}
            </Button>
            <SConModal
              disable={loading && loading !== "PASS"}
              title={t("common.tip")}
              content={t("reimburse.submitConfirm")}
              onOk={() => Promise.resolve(handleSubmit())}
            >
              <Button disabled={!!loading} loading={submiting} type="primary">
                {detail.status === ApproveStatusEnum.DRAFT
                  ? t("reimburse.submit")
                  : t("reimburse.reSubmit")}
              </Button>
            </SConModal>
          </Space>
        )}
      {loading && (
        <Modal
          title={
            loading === "PASS"
              ? t("reimburse.pass")
              : loading === "REJECT"
                ? t("reimburse.reject")
                : loading === "RETURN"
                  ? t("reimburse.return")
                  : t("reimburse.approveRemark")
          }
          onCancel={() => setLoading(undefined)}
          onOk={handleApprove}
          open={!!loading}
          width={600}
        >
          <Form.Item label={t("reimburse.approveRemark")} layout="vertical">
            <Input.TextArea
              value={approveComment}
              onChange={(e) => setApproveComment(e.target.value)}
              autoSize={{ minRows: 4, maxRows: 6 }}
              maxLength={500}
              showCount
              placeholder={t("reimburse.plApproveRemark")}
            />
          </Form.Item>
        </Modal>
      )}
    </Flex>
  );
};

export default ApproveHeader;
